import { DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR } from "@/constants";
import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
import { growaveConfigurationToken } from "@/tokens";
import { isShadowRoot } from "@/utils/isShadowRoot/isShadowRoot";
import { TRANSLATION_NAMESPACES } from "@apps/rewards/constants/constants";
import RewardsAmountFormatter from "@apps/rewards/lib/RewardsAmountFormatter/RewardsAmountFormatter";
import RewardsEarnPointsApiService from "@apps/rewards/modules/EarnPoints/api_service/RewardsEarnPointsApiService";
import RewardsEarnPointsQuery from "@apps/rewards/modules/EarnPoints/query/RewardsEarnPointsQuery";
import RewardsEarnPointsService from "@apps/rewards/modules/EarnPoints/service/RewardsEarnPointsService";
import RewardsStorefrontValuesService from "@apps/rewards/modules/StorefrontValues/service/RewardsStorefrontValuesService";
import RewardsDiResolverService from "@apps/rewards/services/RewardsDiResolverService/RewardsDIResolverService";
import { drawerManagerToken } from "@apps/rewards/tokens";
import type { RewardsEarnPointsProductPageWidgetConfigInterface } from "@apps/rewards/widgets/rewards_earn_points_product_page_widget/RewardsEarnPointsProductPageWidgetConfig";
import RewardsPopUpWidgetLauncherManager from "@apps/rewards/widgets/rewards_popup_widget/utils/RewardsPopUpWidgetLauncherManager/RewardsPopUpWidgetLauncherManager";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import type { ProductVariantIdManagerInterface } from "@lib/ProductVariantIdManager/ProductVariantIdManagerInterface";
import { I18nModule } from "@modules/i18n/I18nModule";
import type { Observable } from "rxjs";
import type { SvelteComponent } from "svelte";
import { injectable } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";
import type { WidgetInterface } from "@interfaces/WidgetInterface";

import RewardsEarnPointsStore from "../../modules/EarnPoints/store/RewardsEarnPointsStore";
import RewardsEarnPointsProductPageWidgetComponent from "./RewardsEarnPointsProductPageWidget.svelte";

@injectable()
class RewardsEarnPointsProductPageWidget implements WidgetInterface {
    private component: SvelteComponent | null;

    constructor(
        private readonly config: RewardsEarnPointsProductPageWidgetConfigInterface,
        private readonly logger: LoggerInterface,
        private readonly productVariantsIdManager: ProductVariantIdManagerInterface,
        private readonly productQuantity$: Observable<string | null>
    ) {
        this.logger.debug("RewardsEarnPointsProductPageWidget is initialized");
        this.component = null;
    }

    async init(): Promise<this> {
        const widgetNamespace =
            TRANSLATION_NAMESPACES.criticalEarnPointsProductPageWidget;
        const pointsNamespace = TRANSLATION_NAMESPACES.commonCriticalPoints;

        const diResolverService = new RewardsDiResolverService();
        const {
            earnPointsApiService,
            i18nModule,
            customizationManager,
            launcherManager,
            drawerManager,
            amountFormatter,
            storefrontValuesService,
            growaveConfiguration,
        } = diResolverService.resolveAll({
            earnPointsApiService: RewardsEarnPointsApiService,
            i18nModule: I18nModule,
            customizationManager: CustomizationManager,
            launcherManager: RewardsPopUpWidgetLauncherManager,
            drawerManager: drawerManagerToken,
            amountFormatter: RewardsAmountFormatter,
            storefrontValuesService: RewardsStorefrontValuesService,
            growaveConfiguration: growaveConfigurationToken,
        });

        const earnPointsStore = new RewardsEarnPointsStore();
        const earnPointsQuery = new RewardsEarnPointsQuery(earnPointsStore);
        const earnPointsService = new RewardsEarnPointsService(
            this.logger,
            earnPointsApiService,
            earnPointsStore
        );

        const { i18n, i18nCriticalLoaderFactory } = i18nModule.exports;
        i18n.registerLoader(
            widgetNamespace,
            i18nCriticalLoaderFactory.getLoader(widgetNamespace)
        );
        i18n.registerLoader(
            pointsNamespace,
            i18nCriticalLoaderFactory.getLoader(pointsNamespace)
        );

        const element = this.config.target;
        const shadowRoot = isShadowRoot(
            this.config.isWidgetShadowRootEnabled,
            growaveConfiguration.isShadowRootDisabledGlobally
        )
            ? element.shadowRoot || element.attachShadow({ mode: "open" })
            : null;

        const customizationManagerConfig = {
            widgetName:
                REWARDS_WIDGET_NAMES.REWARDS_EARN_POINTS_PRODUCT_PAGE_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromBlockCustomCss({
            element,
            shadowRoot,
            scriptSelector: DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR,
        });
        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );
        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        this.component = new RewardsEarnPointsProductPageWidgetComponent({
            target: shadowRoot || element,
            props: {
                earnPointsService,
                earnPointsQuery,
                storefrontValuesService,
                launcherManager,
                drawerManager,
                amountFormatter,
                productId: this.config.productId,
                productVariantIdManager: this.productVariantsIdManager,
                productQuantity$: this.productQuantity$,
                namespace: widgetNamespace,
                pointsNamespace,
                i18n,
                afterWidgetInject: () =>
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    ),
            },
        });

        return this;
    }
}

export default RewardsEarnPointsProductPageWidget;
